import React from "react";
import {
  Badge,
  Button,
  Icon,
  IconButton,
  TextField,
  Toggle,
} from "stories/Admin";
import KidsAudioField from "./KidsAudioField";
import { AdhanPhrase, emptyPhrase, move } from "./kidsTypes";
import styles from "./KidsPage.module.scss";

interface Props {
  phrases: AdhanPhrase[];
  onChange: (phrases: AdhanPhrase[]) => void;
  itemId: string;
}

/** Frasene i adhan: legg til, endre, fjern, flytt og last opp lyd per frase. */
const AdhanPhrasesEditor: React.FC<Props> = ({ phrases, onChange, itemId }) => {
  const update = (i: number, patch: Partial<AdhanPhrase>) =>
    onChange(phrases.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  return (
    <div className={styles.phrases}>
      {phrases.map((p, i) => (
        <div key={i} className={styles.phrase}>
          <div className={styles.phraseHead}>
            <b>Frase {i + 1}</b>
            <Badge tone="blue">×{p.repeat}</Badge>
            {p.fajrOnly && <Badge tone="gold">Bare Fajr</Badge>}
            <span className={styles.spacer} />
            <IconButton
              type="button"
              size="sm"
              label="Flytt opp"
              icon={<span aria-hidden="true">↑</span>}
              disabled={i === 0}
              onClick={() => onChange(move(phrases, i, i - 1))}
            />
            <IconButton
              type="button"
              size="sm"
              label="Flytt ned"
              icon={<span aria-hidden="true">↓</span>}
              disabled={i === phrases.length - 1}
              onClick={() => onChange(move(phrases, i, i + 1))}
            />
            <IconButton
              type="button"
              size="sm"
              variant="danger"
              label="Fjern frasen"
              icon={<Icon name="trash" size={14} />}
              onClick={() => onChange(phrases.filter((_, j) => j !== i))}
            />
          </div>
          <TextField
            label="Arabisk (med tashkeel)"
            lang="ar"
            dir="rtl"
            className={styles.arabicInput}
            value={p.arabic}
            onChange={(e) => update(i, { arabic: e.target.value })}
          />
          <div className={styles.row2}>
            <TextField
              label="Transliterasjon"
              value={p.transliteration ?? ""}
              onChange={(e) => update(i, { transliteration: e.target.value })}
            />
            <TextField
              label="Antall ganger"
              type="number"
              min={1}
              max={10}
              value={String(p.repeat)}
              onChange={(e) =>
                update(i, { repeat: Number(e.target.value) || 1 })
              }
            />
          </div>
          <div className={styles.row3}>
            <TextField
              label="Betydning (norsk)"
              value={p.meaningNb ?? ""}
              onChange={(e) => update(i, { meaningNb: e.target.value })}
            />
            <TextField
              label="Betydning (engelsk)"
              value={p.meaningEn ?? ""}
              onChange={(e) => update(i, { meaningEn: e.target.value })}
            />
            <TextField
              label="Betydning (urdu)"
              lang="ur"
              dir="rtl"
              value={p.meaningUr ?? ""}
              onChange={(e) => update(i, { meaningUr: e.target.value })}
            />
          </div>
          <Toggle
            label="Bare i Fajr-adhan (vises som merknad)"
            checked={p.fajrOnly}
            onChange={(e) => update(i, { fajrOnly: e.target.checked })}
          />
          <KidsAudioField
            compact
            label="Lyd for frasen (valgfri)"
            value={p.audioUrl ?? ""}
            onChange={(url) => update(i, { audioUrl: url || null })}
            itemId={itemId}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={<Icon name="plus" size={14} />}
        onClick={() => onChange([...phrases, emptyPhrase()])}
      >
        Legg til frase
      </Button>
    </div>
  );
};

export default AdhanPhrasesEditor;
